import React, { useEffect, useState } from "react";
import {
  Box,
  Divider,
  Drawer,
  IconButton,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Typography,
  useTheme,
} from "@mui/material";
import {
  SettingsOutlined,
  ChevronLeft,
  ChevronRightOutlined,
  HomeOutlined,
  ReceiptLongOutlined,
  PointOfSaleOutlined,
  TrendingUpOutlined,
  PieChartOutlined,
  PeopleAltOutlined,
} from "@mui/icons-material";
import GroupsOutlinedIcon from "@mui/icons-material/GroupsOutlined";
import RequestQuoteOutlinedIcon from "@mui/icons-material/RequestQuoteOutlined";
import TranslateOutlinedIcon from "@mui/icons-material/TranslateOutlined";
import AssignmentIndOutlinedIcon from "@mui/icons-material/AssignmentIndOutlined";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";
import { useLocation, useNavigate } from "react-router-dom";
import FlexBetween from "./FlexBetween";
import BarChartIcon from "@mui/icons-material/BarChart";

const navItems = [
  {
    text: "Dashboard",
    url: "dashboard",
    icon: <HomeOutlined />,
  },
  {
    text: "Operations",
    icon: null,
  },
  {
    text: "Translators",
    url: "translators",
    icon: <AssignmentIndOutlinedIcon />,
  },
  {
    text: "Clients",
    url: "clients",
    icon: <PeopleAltOutlined />,
  },
  {
    text: "Quotes",
    url: "quotes",
    icon: <RequestQuoteOutlinedIcon />,
  },
  {
    text: "Jobs",
    url: "jobs",
    icon: <ReceiptLongOutlined />,
  },
  {
    text: "Calendar",
    url: "calendar",
    icon: <CalendarMonthIcon />,
  },
  {
    text: "Quickbooks - Soon!",
    icon: null,
  },
  {
    text: "Invoices",
    url: "invoices",
    icon: <PointOfSaleOutlined />,
    disabled: true,
  },
  {
    text: "Expenses",
    url: "expenses",
    icon: <PieChartOutlined />,
    disabled: true,
  },
  {
    text: "Statistics",
    icon: null,
  },
  {
    text: "Overview",
    url: "overview",
    icon: <TrendingUpOutlined />,
  },
  {
    text: "Insights",
    url: "insights",
    icon: <BarChartIcon />,
  },
  {
    text: "Management",
    icon: null,
  },
  {
    text: "Organisation",
    url: "organisation",
    icon: <GroupsOutlinedIcon />,
  },
  {
    text: "Languages",
    url: "languages",
    icon: <TranslateOutlinedIcon />,
  },
  {
    text: "Admins",
    url: "admins",
    icon: <SettingsOutlined />,
  },
  {
    text: "Pending Vendors",
    url: "pending-vendors",
    icon: <AssignmentIndOutlinedIcon />,
  },
];

const Sidebar = ({
  user,
  organisation,
  drawerWidth,
  isSidebarOpen,
  setIsSidebarOpen,
  isNonMobile,
}) => {
  const { pathname } = useLocation();
  const [active, setActive] = useState("");
  const navigate = useNavigate();
  const theme = useTheme();

  useEffect(() => {
    setActive(pathname.substring(1));
  }, [pathname]);

  return (
    <Box component="nav">
      {isSidebarOpen && (
        <Drawer
          open={isSidebarOpen}
          onClose={() => setIsSidebarOpen(false)}
          variant="persistent"
          anchor="left"
          sx={{
            width: drawerWidth,
            "& .MuiDrawer-paper": {
              color: theme.palette.secondary[200],
              backgroundColor: theme.palette.background.alt,
              boxSizing: "border-box",
              borderWidth: isNonMobile ? "0px" : "2px",
              width: drawerWidth,
            },
          }}
        >
          <Box width="100%">
            <Box m="1.5rem 2rem 2rem 1.5rem">
              <FlexBetween
                color={theme.palette.secondary.main}
                sx={{ justifyContent: "center !important" }}
              >
                <Box display="flex" alignItems="center" gap="0.5rem">
                  <Typography variant="h4" fontWeight="bold" textAlign="center">
                    FLOW DYNAMO
                  </Typography>
                </Box>
                {!isNonMobile && (
                  <IconButton onClick={() => setIsSidebarOpen(!isSidebarOpen)}>
                    <ChevronLeft />
                  </IconButton>
                )}
              </FlexBetween>
            </Box>
            <List
              className="nav-list"
              sx={{
                height: "79vh",
                overflowY: "scroll",
              }}
            >
              {navItems.map(({ text, url, icon, disabled = false }) => {
                if (!icon) {
                  return (
                    <Typography key={text} sx={{ m: "2.25rem 0 1rem 3rem" }}>
                      {text}
                    </Typography>
                  );
                }
                const lcText = url.toLowerCase();

                return (
                  <ListItem
                    key={text}
                    disablePadding
                    disabled={disabled ? true : false}
                  >
                    <ListItemButton
                      onClick={() => {
                        if (!disabled) {
                          navigate(`/${lcText}`);
                        }
                      }}
                      sx={{
                        backgroundColor:
                          active === lcText
                            ? theme.palette.secondary[300]
                            : "transparent",
                        color:
                          active === lcText
                            ? theme.palette.primary[600]
                            : theme.palette.secondary[100],
                      }}
                    >
                      <ListItemIcon
                        sx={{
                          ml: "2rem",
                          color:
                            active === lcText
                              ? theme.palette.primary[600]
                              : theme.palette.secondary[200],
                        }}
                      >
                        {icon}
                      </ListItemIcon>
                      <ListItemText primary={text} />
                      {active === lcText && (
                        <ChevronRightOutlined sx={{ ml: "auto" }} />
                      )}
                    </ListItemButton>
                  </ListItem>
                );
              })}
            </List>
          </Box>

          <Box
            position="absolute"
            bottom="2rem"
            width="100%"
            height="6vh"
            backgroundColor={theme.palette.background.alt}
          >
            <Divider />
            <FlexBetween
              textTransform="none"
              gap="1rem"
              m="1.5rem 2rem 0rem 3rem"
            >
              <Box textAlign="left">
                <Typography
                  fontWeight="bold"
                  fontSize="0.9rem"
                  sx={{ color: theme.palette.secondary[100] }}
                >
                  {user.name}
                </Typography>
                <Typography
                  fontSize="0.8rem"
                  sx={{ color: theme.palette.secondary[200] }}
                >
                  {organisation.name}
                </Typography>
              </Box>
              <SettingsOutlined
                onClick={() => navigate("/settings")}
                sx={{
                  color: theme.palette.secondary[300],
                  fontSize: "25px",
                  cursor: "pointer",
                }}
              />
            </FlexBetween>
          </Box>
        </Drawer>
      )}
    </Box>
  );
};

export default Sidebar;
